import { t } from 'i18next';

// Components
import Table from '../../atoms/Table/Table';
import MarkdownContainer from '../../atoms/MarkdownContainer/MarkdownContainer';

// Domain
import { renderCellByType } from '../../../domain/simpleTable/renders/renderCellByType';
import { getSimpleTableColCount } from '../../../domain/simpleTable/simpleTableUseCase';

// Styles
import styles from './SimpleTable.module.css';
import cn from '../../../utils/cn';

/**
 * @param {Array<{ rows?: Array<{ cells?: unknown[] }> }>} groups
 * @returns {boolean}
 */
const hasAnyRows = (groups) => groups.some(
  (group) => Array.isArray(group?.rows)
    && group.rows.some((row) => row && Array.isArray(row.cells)),
);

/**
 * SimpleTable component - Renders a simple table for detail header and dashboard
 * @param {object} props - Component props
 * @param {ApiDetailHeaderFieldTableTable | ApiDashboardItemTableTable} props.table - Table data
 * @param {'dark' | 'dashboard'} [props.variant='dashboard'] - Table variant for styling (dark for header, dashboard for dashboard cards)
 * @param {string} [props.className] - Additional CSS classes
 * @param {object} [props.containerProps] - Props for the container wrapper
 * @param {boolean} [props.showEmptyMessage=true] - Show message if empty
 * @returns {JSX.Element | null}
 */
function SimpleTable({
  table,
  variant = 'dashboard',
  className,
  containerProps = {},
  showEmptyMessage = true,
}) {
  if (!table) {
    return null;
  }

  const { headers, groups, footers } = table;
  const hasRows = hasAnyRows(groups);
  const isEmpty = headers.length === 0 && !hasRows && (!footers || footers.length === 0);

  if (isEmpty && showEmptyMessage) {
    return (
      <p className={styles['empty-message']}>
        {t('detailHeaderTable.empty')}
      </p>
    );
  }

  if (isEmpty) {
    return null;
  }

  const containerClassName = /** @type {{ className?: string }} */ (containerProps || {}).className;
  const colCount = getSimpleTableColCount(table);

  return (
    <div
      className={cn([
        styles['simple-table-wrapper'],
        styles[`simple-table-wrapper-${variant}`],
        className,
        containerClassName,
      ])}
    >
      <Table width="100%">
        <>
          {/* Headers */}
          {headers.length > 0 && (
            <thead>
              <tr>
                {headers.map((header) => {
                  // TODO : How to crerate unique key without ID in the object and without using index ?
                  const headerKey = `header-${[header.value, header.width, header.align].filter(Boolean).join('|')}`;

                  return (
                    <th
                      key={headerKey}
                      className={cn([
                        styles['table-header-cell'],
                        styles[`table-header-cell-${variant}`],
                      ])}
                      style={{
                        width: 'auto',
                        textAlign: /** @type {'left' | 'center' | 'right'} */ (header.align || 'left'),
                      }}
                    >
                      <MarkdownContainer content={header.value} />
                    </th>
                  );
                })}
              </tr>
            </thead>
          )}

          {/* Groups and Rows */}
          {hasRows && groups.map((group, groupIndex) => {
            if (!group?.rows || !Array.isArray(group.rows)) {
              return null;
            }

            const validRows = group.rows.filter(
              (row) => row && row.cells && Array.isArray(row.cells),
            );

            if (!group.lib && validRows.length === 0) {
              return null;
            }

            return (
              <tbody key={group.lib || `group-${groupIndex}`}>
                {variant === 'dashboard' ? (
                  <tr className={styles['table-group-spacer-row']}>
                    <td className={styles['table-group-spacer']} colSpan={colCount || 1} aria-hidden="true" />
                  </tr>
                ) : null}
                {group.lib ? (
                  <tr className={styles['table-group-header-row']}>
                    <td
                      className={cn([
                        styles['table-group-header'],
                        styles[`table-group-header-${variant}`],
                      ])}
                      colSpan={colCount || 1}
                    >
                      <MarkdownContainer content={group.lib} />
                    </td>
                  </tr>
                ) : null}
                {validRows.map((row, rowIndex) => {
                  const key = `group-${group.lib || groupIndex}-row-${rowIndex}`;
                  return (
                    <tr key={key} className={styles['table-data-row']}>
                      {row.cells.map((cell, cellIndex) => {
                        const header = headers[cellIndex];
                        const align = /** @type {'left' | 'center' | 'right'} */ (header?.align || 'left');
                        const cellKey = `${key}-cell-${cellIndex}`;
                        return (
                          <td
                            key={cellKey}
                            className={cn([
                              styles['table-body-cell'],
                              styles[`table-body-cell-${variant}`],
                            ])}
                            style={{
                              width: 'auto',
                              textAlign: /** @type {'left' | 'center' | 'right'} */ (align),
                            }}
                          >
                            {renderCellByType(
                              cell,
                              /** @type {'left' | 'center' | 'right'} */ (align),
                              { markdownInline: variant === 'dark' },
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            );
          })}

          {/* Footers */}
          {footers && footers.length > 0 && (
            <tfoot>
              <tr>
                {footers.map((footer) => (
                  <td
                    key={`footer-${footer.value}-${footer.align || 'left'}`}
                    className={cn([
                      styles['table-footer-cell'],
                      styles[`table-footer-cell-${variant}`],
                    ])}
                    style={{
                      textAlign: /** @type {'left' | 'center' | 'right'} */ (footer.align || 'left'),
                    }}
                  >
                    <MarkdownContainer content={footer.value} />
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </>
      </Table>
    </div>
  );
}

export default SimpleTable;
